import { useState, useEffect, useRef, useCallback } from "react";
import {
  Combobox,
  Loader,
  useCombobox,
  CloseButton,
  PillsInput,
  Pill,
  Group,
  CheckIcon,
} from "@mantine/core";
import { searchValueListByCode } from "../../../api";
import { useDebouncedValue } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

// Async loader logic now uses search endpoint
function getAsyncData(attrib, searchQuery = "", limit = 50) {
  const listCode = attrib.typeParameters.find(
    (p) => p.name === "LIST_CODE"
  )?.value;
  return searchValueListByCode(
    attrib.destinationMarketplaceId,
    listCode,
    searchQuery,
    limit
  );
}

/**
 * Multi-value version of ValueListInput built with Mantine Combobox + PillsInput.
 * Stores an array of selected labels in form field attrib.code (mirrors single-select storing label).
 * Now uses server-side search for better performance with large lists.
 */
export default function ValueMultiListInput({
  form,
  attrib,
  value: valueProp,
  error,
  limit = null,
  maxValues = null,
}) {
  const { t } = useTranslation("pim");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]); // {value, label}[]
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const inputRef = useRef(null);
  const selectedRef = useRef([]);

  // Ensure field is an array in form state
  const rawValue = valueProp ?? form.getValues()[attrib.code];
  const selectedValues = Array.isArray(rawValue)
    ? rawValue
    : rawValue
    ? [rawValue]
    : [];
  selectedRef.current = selectedValues;

  const setArrayValue = useCallback(
    (next) => {
      const currentValues = form.getValues();
      form.setValues({
        ...currentValues,
        [attrib.code]: next,
      });
    },
    [form, attrib.code]
  );

  useEffect(() => {
    setSearch("");
  }, [selectedValues]);

  // If form field was not array, normalize it once
  useEffect(() => {
    if (!Array.isArray(rawValue)) {
      setArrayValue(selectedValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch("");
    },
    onDropdownOpen: () => {
      // Load initial data when dropdown opens
      if (data.length === 0 && !loading) {
        setLoading(true);
        getAsyncData(attrib, "", limit ?? 50).then((response) => {
          const mapped = response.values.map((p) => ({
            value: p.code,
            label: p.label,
          }));
          setData(mapped);
          setLoading(false);
          combobox.resetSelectedOption();
        });
      }
    },
  });

  // Fetch data when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== "" || data.length > 0) {
      setLoading(true);
      getAsyncData(attrib, debouncedSearch, limit ?? 50).then((response) => {
        const mapped = response.values.map((p) => ({
          value: p.code,
          label: p.label,
        }));
        setData(mapped);
        setLoading(false);
        combobox.updateSelectedOptionIndex();
      });
    }
  }, [debouncedSearch, attrib, limit]);

  const toggleValue = (label) => {
    const current = selectedRef.current;
    const exists = current.includes(label);
    let next;
    if (exists) {
      next = current.filter((v) => v !== label);
    } else {
      if (maxValues && current.length >= maxValues) return; // respect maxValues limit if provided
      next = [...current, label];
    }
    selectedRef.current = next;
    setArrayValue(next);
  };

  const clearAll = () => {
    selectedRef.current = [];
    setArrayValue([]);
    setSearch("");
  };

  const options = data.map((item) => {
    const selected = selectedRef.current.includes(item.label);
    return (
      <Combobox.Option
        value={item.label}
        key={item.value}
        active={selected}
        aria-selected={selected}
      >
        <Group gap="sm">
          {selected && <CheckIcon color="gray" size={12} />}
          <span>{item.label}</span>
        </Group>
      </Combobox.Option>
    );
  });

  const pills = selectedValues.map((v) => (
    <Pill key={v} withRemoveButton onRemove={() => toggleValue(v)}>
      {v}
    </Pill>
  ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(val) => {
        toggleValue(val);
        // Keep dropdown open for multi selection; clear search & refresh option highlight
        setSearch("");
        combobox.updateSelectedOptionIndex();
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }}
    >
      <Combobox.Target>
        <PillsInput
          onClick={() => combobox.openDropdown()}
          error={error}
          rightSection={
            loading ? (
              <Loader size={18} />
            ) : selectedRef.current.length > 0 ? (
              <CloseButton
                size="sm"
                onMouseDown={(e) => e.preventDefault()}
                onClick={clearAll}
                aria-label={t("input.clearValues")}
              />
            ) : (
              <Combobox.Chevron onMouseDown={(e) => e.preventDefault()} />
            )
          }
        >
          <Pill.Group>
            {pills}
            <Combobox.EventsTarget>
              <PillsInput.Field
                ref={inputRef}
                value={search}
                onChange={(e) => {
                  setSearch(e.currentTarget.value);
                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === "Backspace" &&
                    search === "" &&
                    selectedRef.current.length > 0
                  ) {
                    // Remove last selected value
                    const current = selectedRef.current;
                    toggleValue(current[current.length - 1]);
                    // Prevent browser navigating back when field empty
                    e.preventDefault();
                    // Keep focus on input after removing pill using setTimeout to ensure it happens after React update
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 0);
                  } else if (e.key === "Escape") {
                    combobox.closeDropdown();
                    setSearch("");
                  } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    // Prevent default to avoid skipping options - let combobox handle it
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onFocus={() => combobox.openDropdown()}
                placeholder={
                  selectedRef.current.length === 0
                    ? t("input.searchValues")
                    : ""
                }
                autoComplete="off"
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options style={{ maxHeight: 200, overflowY: "auto" }}>
          {loading ? (
            <Combobox.Empty>{t("common.loading")}</Combobox.Empty>
          ) : options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>{t("input.noMatches")}</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
